import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Plus,
  Save,
  Trash2,
  Edit,
  X,
  Search,
  Link as LinkIcon,
  Copy,
  Ban,
  CheckCircle2,
  ArrowRightCircle,
  Send,
  Settings,
  StickyNote,
  Lock
} from 'lucide-react';
import { useNotifications } from '../../hooks/useNotifications';
import { useConfirm } from '../../contexts/ConfirmContext';
import Breadcrumbs from '../../components/Admin/Breadcrumbs';
import I18nTripleFields, { I18nTriple } from '../../components/Admin/I18nTripleFields';
import { emptyI18nTriple } from '../../i18n/localizeContent';
import {
  MAX_PARCELAS,
  MIN_PARCELAS,
  OpcaoCalculada,
  OpcoesPagamento,
  calcularOpcoes,
  emptyOpcoesPagamento,
  formatEuro,
  calcularTotais,
  AbatimentoManual,
  labelOpcaoAdmin,
  temOpcoes
} from '../../utils/orcamentoPagamento';

interface ItemLocaleBlock {
  descricao?: string;
}

interface ItemI18n {
  pt: ItemLocaleBlock;
  en: ItemLocaleBlock;
  es: ItemLocaleBlock;
}

interface OrcamentoItem {
  descricao: string;
  i18n?: ItemI18n;
  servicoId?: string | null;
  valor: number;
  quantidade: number;
  abatidoAoFecharOutros?: boolean;
}

interface ServicoOption {
  _id: string;
  nome: string;
  i18n?: I18nTriple;
  valorPadrao: number;
  abatidoAoFecharOutros?: boolean;
}

type OrcamentoStatus = 'draft' | 'sent' | 'accepted' | 'converted' | 'expired' | 'revoked' | 'declined';
type MotivoRecusa = 'preco' | 'condicoes_pagamento' | 'outro_fornecedor' | 'adiou' | 'desistiu' | 'outro';

interface RecusaOrcamento {
  motivo: MotivoRecusa;
  comentario?: string;
  recusadoAt?: string;
}
type FormaPagamento = 'avista' | 'parcelado_2' | 'parcelado_3' | 'personalizado';
type OrigemCliente =
  | 'indicacao'
  | 'instagram'
  | 'facebook'
  | 'tiktok'
  | 'youtube'
  | 'google'
  | 'landing_page'
  | 'site'
  | 'whatsapp'
  | 'evento'
  | 'cliente_antigo'
  | 'outro';

interface OrcamentoData {
  _id?: string;
  nomeCliente: string;
  emailCliente?: string;
  telefoneCliente?: string;
  origemCliente?: OrigemCliente | '';
  origemDetalhe?: string;
  notasInternas?: string;
  itens: OrcamentoItem[];
  formaPagamento: FormaPagamento;
  opcoesPagamento?: OpcoesPagamento;
  abatimentoManual?: AbatimentoManual;
  opcoesCalculadas?: OpcaoCalculada[];
  opcaoEscolhida?: OpcaoCalculada | null;
  corpoTexto: string;
  i18n?: I18nTriple;
  idioma?: 'pt' | 'en' | 'es';
  status?: OrcamentoStatus;
  recusa?: RecusaOrcamento | null;
  total?: number;
  createdAt?: string;
  viewUrl?: string;
}

const FORMAS_PAGAMENTO: { value: FormaPagamento; label: string }[] = [
  { value: 'avista', label: 'À vista' },
  { value: 'parcelado_2', label: 'Parcelado em 2 vezes' },
  { value: 'parcelado_3', label: 'Parcelado em 3 vezes' },
  { value: 'personalizado', label: 'Personalizado' }
];

const labelFormaPagamento = (forma?: string) =>
  FORMAS_PAGAMENTO.find((f) => f.value === forma)?.label || forma || '—';

const STATUS_LABEL: Record<OrcamentoStatus, { label: string; className: string }> = {
  draft: { label: 'Rascunho', className: 'bg-gray-100 text-gray-600' },
  sent: { label: 'Aguardando cliente', className: 'bg-amber-100 text-amber-700' },
  accepted: { label: 'Aceito', className: 'bg-green-100 text-green-700' },
  converted: { label: 'Convertido em contrato', className: 'bg-blue-100 text-blue-700' },
  expired: { label: 'Link expirado', className: 'bg-gray-100 text-gray-600' },
  revoked: { label: 'Link revogado', className: 'bg-gray-100 text-gray-600' },
  declined: { label: 'Recusado', className: 'bg-red-100 text-red-700' }
};

// Espelho de MOTIVOS_RECUSA em backend/models/Orcamento.js
const MOTIVO_RECUSA_LABEL: Record<MotivoRecusa, string> = {
  preco: 'Valor acima do esperado',
  condicoes_pagamento: 'Condições de pagamento',
  outro_fornecedor: 'Fechou com outro escritório/profissional',
  adiou: 'Vai deixar para mais tarde',
  desistiu: 'Desistiu do processo',
  outro: 'Outro motivo'
};

// Espelho de ORIGENS_CLIENTE em backend/models/Orcamento.js (e ORIGEM_CLIENTE_LABEL em routes/orcamento.js)
const ORIGEM_CLIENTE_LABEL: Record<OrigemCliente, string> = {
  indicacao: 'Indicação',
  instagram: 'Instagram',
  facebook: 'Facebook',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  google: 'Google',
  landing_page: 'Landing page',
  site: 'Site',
  whatsapp: 'WhatsApp',
  evento: 'Evento/palestra',
  cliente_antigo: 'Cliente antigo',
  outro: 'Outro'
};

const ORIGEM_DETALHE_PLACEHOLDER: Partial<Record<OrigemCliente, string>> = {
  indicacao: 'Quem indicou?',
  landing_page: 'Qual landing page / campanha?',
  evento: 'Qual evento?',
  outro: 'Descreva a origem'
};

const labelOrigem = (o: Pick<OrcamentoData, 'origemCliente' | 'origemDetalhe'>) =>
  [o.origemCliente ? ORIGEM_CLIENTE_LABEL[o.origemCliente] : '', o.origemDetalhe].filter(Boolean).join(' — ');

const temInfoInterna = (o: OrcamentoData) => !!(o.origemCliente || o.origemDetalhe || o.notasInternas);

interface InternoForm {
  origemCliente: OrigemCliente | '';
  origemDetalhe: string;
  notasInternas: string;
}

// Origem + notas internas: usado no formulário do orçamento e no modal de edição rápida da lista
const CamposInternos: React.FC<{ value: InternoForm; onChange: (v: InternoForm) => void }> = ({ value, onChange }) => (
  <div className="space-y-3">
    <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-3">
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">De onde veio o cliente</label>
        <select
          value={value.origemCliente}
          onChange={(e) => onChange({ ...value, origemCliente: e.target.value as OrigemCliente | '' })}
          className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 bg-white"
        >
          <option value="">Não informado</option>
          {(Object.keys(ORIGEM_CLIENTE_LABEL) as OrigemCliente[]).map((k) => (
            <option key={k} value={k}>
              {ORIGEM_CLIENTE_LABEL[k]}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Detalhe da origem</label>
        <input
          value={value.origemDetalhe}
          maxLength={200}
          placeholder={(value.origemCliente && ORIGEM_DETALHE_PLACEHOLDER[value.origemCliente]) || 'Opcional'}
          onChange={(e) => onChange({ ...value, origemDetalhe: e.target.value })}
          className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 bg-white"
        />
      </div>
    </div>
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">Notas internas</label>
      <textarea
        value={value.notasInternas}
        maxLength={2000}
        rows={3}
        placeholder="Contexto da negociação, perfil do cliente, próximos passos..."
        onChange={(e) => onChange({ ...value, notasInternas: e.target.value })}
        className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 bg-white"
      />
      <p className="text-xs text-gray-500 text-right">{value.notasInternas.length}/2000</p>
    </div>
  </div>
);

const emptyItem = (): OrcamentoItem => ({ descricao: '', valor: 0, quantidade: 1 });

const emptyForm = (): OrcamentoData => ({
  nomeCliente: '',
  emailCliente: '',
  telefoneCliente: '',
  origemCliente: '',
  origemDetalhe: '',
  notasInternas: '',
  itens: [emptyItem()],
  formaPagamento: 'parcelado_3',
  opcoesPagamento: emptyOpcoesPagamento(),
  abatimentoManual: { valor: 0, descricao: '' },
  corpoTexto: '',
  idioma: 'pt'
});

// Orçamentos antigos só têm `formaPagamento`: converte para as opções equivalentes ao editar.
const opcoesFromLegado = (forma: FormaPagamento): OpcoesPagamento => {
  const opcoes = emptyOpcoesPagamento();
  if (forma === 'parcelado_2' || forma === 'parcelado_3') {
    opcoes.avista.habilitado = false;
    opcoes.parcelados = [{ parcelas: forma === 'parcelado_2' ? 2 : 3, juros: 0 }];
  }
  return opcoes;
};

const servicoOptionLabel = (s: ServicoOption) => `${s.i18n?.pt?.nome || s.nome} (€${s.valorPadrao.toFixed(2)})`;

const computeTotal = (itens: OrcamentoItem[], abatimentoManual?: AbatimentoManual) =>
  calcularTotais(itens, abatimentoManual).total;

const AdminOrcamento: React.FC = () => {
  const navigate = useNavigate();
  const notifications = useNotifications();
  const confirm = useConfirm();

  const [orcamentos, setOrcamentos] = useState<OrcamentoData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<OrcamentoStatus | ''>('');
  const [origemFilter, setOrigemFilter] = useState<OrigemCliente | ''>('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<OrcamentoData>(emptyForm());
  const [saving, setSaving] = useState(false);

  const [i18n, setI18n] = useState<I18nTriple>(emptyI18nTriple());
  const [servicos, setServicos] = useState<ServicoOption[]>([]);
  const [servicoQuery, setServicoQuery] = useState('');

  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkOrcamentoId, setLinkOrcamentoId] = useState<string | null>(null);
  const [expiresInDays, setExpiresInDays] = useState(30);
  const [sendingLink, setSendingLink] = useState(false);
  const [viewUrlPreview, setViewUrlPreview] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [converting, setConverting] = useState<string | null>(null);

  const [aceiteOrcamento, setAceiteOrcamento] = useState<OrcamentoData | null>(null);
  const [aceiteOpcaoId, setAceiteOpcaoId] = useState('');
  const [marcandoAceito, setMarcandoAceito] = useState(false);

  const [internoOrcamento, setInternoOrcamento] = useState<OrcamentoData | null>(null);
  const [internoForm, setInternoForm] = useState<InternoForm>({ origemCliente: '', origemDetalhe: '', notasInternas: '' });
  const [salvandoInterno, setSalvandoInterno] = useState(false);

  useEffect(() => {
    fetchOrcamentos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, statusFilter, origemFilter]);

  useEffect(() => {
    fetchServicos();
  }, []);

  const fetchServicos = async () => {
    try {
      const res = await axios.get('/servico?ativo=true');
      setServicos(res.data);
    } catch (error) {
      console.error('Erro ao carregar catálogo de serviços:', error);
    }
  };

  const fetchOrcamentos = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: '50' });
      if (searchTerm) params.set('search', searchTerm);
      if (statusFilter) params.set('status', statusFilter);
      if (origemFilter) params.set('origem', origemFilter);
      const res = await axios.get(`/orcamento?${params}`);
      setOrcamentos(res.data.orcamentos);
    } catch (error) {
      console.error(error);
      notifications.error.settingsUpdate('Erro ao carregar orçamentos');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyForm());
    setI18n(emptyI18nTriple());
    setShowForm(true);
  };

  const openEdit = (o: OrcamentoData) => {
    setEditingId(o._id || null);
    setFormData({
      ...o,
      emailCliente: o.emailCliente || '',
      telefoneCliente: o.telefoneCliente || '',
      origemCliente: o.origemCliente || '',
      origemDetalhe: o.origemDetalhe || '',
      notasInternas: o.notasInternas || '',
      itens: o.itens.length > 0 ? o.itens : [emptyItem()],
      abatimentoManual: {
        valor: o.abatimentoManual?.valor || 0,
        descricao: o.abatimentoManual?.descricao || ''
      },
      opcoesPagamento: temOpcoes(o.opcoesPagamento)
        ? {
            avista: { habilitado: !!o.opcoesPagamento?.avista?.habilitado },
            avistaDesconto: {
              habilitado: !!o.opcoesPagamento?.avistaDesconto?.habilitado,
              percentual: o.opcoesPagamento?.avistaDesconto?.percentual || 0
            },
            parcelados: o.opcoesPagamento?.parcelados || []
          }
        : opcoesFromLegado(o.formaPagamento)
    });
    setI18n({
      pt: { corpoTexto: o.i18n?.pt?.corpoTexto || o.corpoTexto || '' },
      en: { corpoTexto: o.i18n?.en?.corpoTexto || '' },
      es: { corpoTexto: o.i18n?.es?.corpoTexto || '' }
    });
    setShowForm(true);
  };

  const handleItemAbatidoChange = (index: number, abatidoAoFecharOutros: boolean) => {
    setFormData((prev) => ({
      ...prev,
      itens: prev.itens.map((item, i) => (i === index ? { ...item, abatidoAoFecharOutros } : item))
    }));
  };

  const handleItemChange = (index: number, field: keyof OrcamentoItem, value: string) => {
    setFormData((prev) => ({
      ...prev,
      itens: prev.itens.map((item, i) =>
        i === index
          ? {
              ...item,
              [field]: field === 'descricao' ? value : Number(value) || 0
            }
          : item
      )
    }));
  };

  const addItem = () => {
    setFormData((prev) => ({ ...prev, itens: [...prev.itens, emptyItem()] }));
  };

  const addItemFromServico = (servicoId: string) => {
    const servico = servicos.find((s) => s._id === servicoId);
    if (!servico) return;
    const ptNome = servico.i18n?.pt?.nome || servico.nome;
    setFormData((prev) => ({
      ...prev,
      itens: [
        ...prev.itens,
        {
          descricao: ptNome,
          i18n: {
            pt: { descricao: ptNome },
            en: { descricao: servico.i18n?.en?.nome || '' },
            es: { descricao: servico.i18n?.es?.nome || '' }
          },
          servicoId: servico._id,
          valor: servico.valorPadrao,
          quantidade: 1,
          abatidoAoFecharOutros: !!servico.abatidoAoFecharOutros
        }
      ]
    }));
  };

  const removeItem = (index: number) => {
    setFormData((prev) => ({ ...prev, itens: prev.itens.filter((_, i) => i !== index) }));
  };

  const opcoes = formData.opcoesPagamento || emptyOpcoesPagamento();

  const updateOpcoes = (fn: (o: OpcoesPagamento) => OpcoesPagamento) => {
    setFormData((prev) => ({ ...prev, opcoesPagamento: fn(prev.opcoesPagamento || emptyOpcoesPagamento()) }));
  };

  const addParcelado = () => {
    updateOpcoes((o) => {
      const usados = new Set(o.parcelados.map((p) => p.parcelas));
      let n = MIN_PARCELAS;
      while (usados.has(n) && n < MAX_PARCELAS) n += 1;
      return { ...o, parcelados: [...o.parcelados, { parcelas: n, juros: 0 }] };
    });
  };

  const updateParcelado = (index: number, field: 'parcelas' | 'juros', value: string) => {
    updateOpcoes((o) => ({
      ...o,
      parcelados: o.parcelados.map((p, i) =>
        i === index ? { ...p, [field]: field === 'parcelas' ? parseInt(value, 10) || 0 : Number(value) || 0 } : p
      )
    }));
  };

  const removeParcelado = (index: number) => {
    updateOpcoes((o) => ({ ...o, parcelados: o.parcelados.filter((_, i) => i !== index) }));
  };

  const validarOpcoes = (o: OpcoesPagamento): string | null => {
    if (!temOpcoes(o)) return 'Habilite ao menos uma opção de pagamento para o cliente.';
    if (o.avistaDesconto.habilitado && (o.avistaDesconto.percentual <= 0 || o.avistaDesconto.percentual > 100)) {
      return 'Informe um desconto à vista entre 0 e 100%.';
    }
    const vistos = new Set<number>();
    for (const p of o.parcelados) {
      if (!Number.isInteger(p.parcelas) || p.parcelas < MIN_PARCELAS || p.parcelas > MAX_PARCELAS) {
        return `O número de parcelas deve estar entre ${MIN_PARCELAS} e ${MAX_PARCELAS}.`;
      }
      if (p.juros < 0 || p.juros > 100) return 'Os juros do parcelado devem estar entre 0 e 100%.';
      if (vistos.has(p.parcelas)) return `Há mais de uma opção em ${p.parcelas}x — deixe apenas uma.`;
      vistos.add(p.parcelas);
    }
    return null;
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const erroOpcoes = validarOpcoes(opcoes);
    if (erroOpcoes) {
      notifications.error.settingsUpdate(erroOpcoes);
      return;
    }
    try {
      setSaving(true);
      const payload = { ...formData, corpoTexto: i18n.pt?.corpoTexto || '', i18n };
      if (editingId) {
        await axios.put(`/orcamento/${editingId}`, payload);
      } else {
        await axios.post('/orcamento', payload);
      }
      notifications.success.settingsUpdated();
      setShowForm(false);
      fetchOrcamentos();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      notifications.error.settingsUpdate(err.response?.data?.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!(await confirm({ title: 'Excluir orçamento', message: 'Este orçamento será excluído permanentemente.', confirmText: 'Excluir', variant: 'danger' }))) return;
    try {
      await axios.delete(`/orcamento/${id}`);
      notifications.success.settingsUpdated();
      fetchOrcamentos();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      notifications.error.settingsUpdate(err.response?.data?.message || 'Erro ao excluir');
    }
  };

  const openSendLink = (id: string) => {
    setLinkOrcamentoId(id);
    setExpiresInDays(30);
    setViewUrlPreview('');
    setLinkCopied(false);
    setShowLinkModal(true);
  };

  const openViewLink = (o: OrcamentoData) => {
    setLinkOrcamentoId(o._id || null);
    setViewUrlPreview(o.viewUrl || '');
    setLinkCopied(false);
    setShowLinkModal(true);
  };

  const confirmSendLink = async () => {
    if (!linkOrcamentoId) return;
    try {
      setSendingLink(true);
      const res = await axios.post(`/orcamento/${linkOrcamentoId}/send-link`, { expiresInDays });
      setViewUrlPreview(res.data.viewUrl);
      notifications.success.settingsUpdated();
      fetchOrcamentos();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      notifications.error.settingsUpdate(err.response?.data?.message || 'Erro ao gerar link');
    } finally {
      setSendingLink(false);
    }
  };

  const copyViewUrl = () => {
    if (!viewUrlPreview) return;
    navigator.clipboard.writeText(viewUrlPreview);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const revokeLink = async () => {
    if (!linkOrcamentoId) return;
    if (!(await confirm({ title: 'Revogar link', message: 'O cliente não poderá mais acessar este orçamento.', confirmText: 'Revogar', variant: 'danger' }))) return;
    try {
      await axios.post(`/orcamento/${linkOrcamentoId}/revoke-link`);
      notifications.success.settingsUpdated();
      setShowLinkModal(false);
      fetchOrcamentos();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      notifications.error.settingsUpdate(err.response?.data?.message || 'Erro ao revogar link');
    }
  };

  const postMarcarAceito = async (id: string, opcaoId?: string) => {
    try {
      setMarcandoAceito(true);
      await axios.post(`/orcamento/${id}/marcar-aceito`, opcaoId ? { opcaoId } : {});
      notifications.success.settingsUpdated();
      setAceiteOrcamento(null);
      fetchOrcamentos();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      notifications.error.settingsUpdate(err.response?.data?.message || 'Erro ao marcar como aceito');
    } finally {
      setMarcandoAceito(false);
    }
  };

  const marcarAceito = async (o: OrcamentoData) => {
    if (!o._id) return;
    // Com opções de pagamento, o admin escolhe a forma pelo cliente num modal
    if (o.opcoesCalculadas && o.opcoesCalculadas.length > 0) {
      setAceiteOrcamento(o);
      setAceiteOpcaoId(o.opcoesCalculadas[0].id);
      return;
    }
    if (!(await confirm({ title: 'Marcar como aceito', message: 'O orçamento será marcado como aceito manualmente.', confirmText: 'Marcar como aceito' }))) return;
    postMarcarAceito(o._id);
  };

  const openInterno = (o: OrcamentoData) => {
    setInternoOrcamento(o);
    setInternoForm({
      origemCliente: o.origemCliente || '',
      origemDetalhe: o.origemDetalhe || '',
      notasInternas: o.notasInternas || ''
    });
  };

  const salvarInterno = async () => {
    if (!internoOrcamento?._id) return;
    try {
      setSalvandoInterno(true);
      const res = await axios.patch(`/orcamento/${internoOrcamento._id}/interno`, internoForm);
      const { origemCliente, origemDetalhe, notasInternas } = res.data;
      setOrcamentos((prev) =>
        prev.map((o) => (o._id === internoOrcamento._id ? { ...o, origemCliente, origemDetalhe, notasInternas } : o))
      );
      notifications.success.settingsUpdated();
      setInternoOrcamento(null);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      notifications.error.settingsUpdate(err.response?.data?.message || 'Erro ao salvar notas internas');
    } finally {
      setSalvandoInterno(false);
    }
  };

  const converterEmContrato = async (id: string) => {
    if (!(await confirm({ title: 'Converter orçamento', message: 'Uma venda e as parcelas serão criadas no CRM.', confirmText: 'Converter' }))) return;
    try {
      setConverting(id);
      const res = await axios.post(`/orcamento/${id}/converter`);
      notifications.success.settingsUpdated();
      navigate('/admin/contrato', { state: { prefillContrato: res.data.prefillContrato } });
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      notifications.error.settingsUpdate(err.response?.data?.message || 'Erro ao converter orçamento');
    } finally {
      setConverting(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <Breadcrumbs />
      </div>

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Orçamento</h1>
          <p className="text-gray-600 mt-1 text-sm">
            Monte um orçamento com itens de serviço e forma de pagamento, envie um link para o cliente aceitar e converta em contrato.
          </p>
        </div>
        {!showForm && (
          <button onClick={openCreate} className="btn-primary flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Novo orçamento
          </button>
        )}
      </div>

      {showForm && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-6">
            <h2 className="text-xl font-semibold">{editingId ? 'Editar orçamento' : 'Novo orçamento'}</h2>
            <button
              onClick={() => setShowForm(false)}
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors duration-200"
              title="Fechar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSave} className="space-y-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Dados do cliente</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                  <input
                    required
                    value={formData.nomeCliente}
                    onChange={(e) => setFormData((prev) => ({ ...prev, nomeCliente: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.emailCliente || ''}
                    onChange={(e) => setFormData((prev) => ({ ...prev, emailCliente: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Opcional — se preenchido, o link é enviado por e-mail automaticamente; senão, copie e envie manualmente.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                  <input
                    value={formData.telefoneCliente || ''}
                    onChange={(e) => setFormData((prev) => ({ ...prev, telefoneCliente: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            <div className="p-4 rounded-lg border border-dashed border-amber-300 bg-amber-50/60">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Lock className="w-4 h-4 text-amber-600" /> Uso interno
              </h3>
              <p className="text-xs text-gray-600 mt-1 mb-3">
                Visível só para a equipe — não aparece no link nem nos e-mails do cliente. Ao converter, vai para a origem e
                as observações do cliente no CRM.
              </p>
              <CamposInternos
                value={{
                  origemCliente: formData.origemCliente || '',
                  origemDetalhe: formData.origemDetalhe || '',
                  notasInternas: formData.notasInternas || ''
                }}
                onChange={(v) => setFormData((prev) => ({ ...prev, ...v }))}
              />
            </div>

            <div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                <h3 className="font-semibold text-gray-900">Itens do orçamento</h3>
                <div className="flex flex-wrap items-center gap-3">
                  {servicos.length > 0 && (
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                      <input
                        type="text"
                        list="orcamento-servicos-datalist"
                        value={servicoQuery}
                        onChange={(e) => {
                          const val = e.target.value;
                          const match = servicos.find((s) => servicoOptionLabel(s) === val);
                          if (match) {
                            addItemFromServico(match._id);
                            setServicoQuery('');
                          } else {
                            setServicoQuery(val);
                          }
                        }}
                        placeholder="Buscar serviço para adicionar..."
                        className="pl-8 pr-3 py-1.5 text-sm border rounded-md focus:ring-2 focus:ring-blue-500 w-64"
                      />
                      <datalist id="orcamento-servicos-datalist">
                        {servicos.map((s) => (
                          <option key={s._id} value={servicoOptionLabel(s)} />
                        ))}
                      </datalist>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={addItem}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-600 hover:text-primary-800"
                  >
                    <Plus className="w-4 h-4" /> Adicionar item manual
                  </button>
                  <Link
                    to="/admin/servicos"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-800"
                  >
                    <Settings className="w-4 h-4" /> Gerenciar serviços
                  </Link>
                </div>
              </div>
              <div className="space-y-3">
                {formData.itens.map((item, index) => (
                  <div key={index} className="grid grid-cols-1 sm:grid-cols-[1fr_140px_100px_auto] gap-3 items-start">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Descrição *</label>
                      <input
                        required
                        value={item.descricao}
                        onChange={(e) => handleItemChange(index, 'descricao', e.target.value)}
                        className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                      />
                      <label className="mt-1 inline-flex items-center gap-2 text-xs text-gray-600">
                        <input
                          type="checkbox"
                          checked={!!item.abatidoAoFecharOutros}
                          onChange={(e) => handleItemAbatidoChange(index, e.target.checked)}
                        />
                        Abater do total se o cliente fechar outros serviços
                      </label>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Valor (€) *</label>
                      <input
                        type="number"
                        min={0}
                        step={0.01}
                        required
                        value={item.valor}
                        onChange={(e) => handleItemChange(index, 'valor', e.target.value)}
                        className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Qtd.</label>
                      <input
                        type="number"
                        min={1}
                        value={item.quantidade}
                        onChange={(e) => handleItemChange(index, 'quantidade', e.target.value)}
                        className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      disabled={formData.itens.length <= 1}
                      className="mt-6 p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors duration-200 disabled:opacity-30"
                      title="Remover item"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
              {(() => {
                const totais = calcularTotais(formData.itens, formData.abatimentoManual);
                return (
                  <div className="text-right mt-3 space-y-0.5">
                    {totais.abatimento > 0 && (
                      <>
                        <p className="text-sm text-gray-600">Subtotal: {formatEuro(totais.subtotal)}</p>
                        <p className="text-sm text-green-700">Abatimento: −{formatEuro(totais.abatimento)}</p>
                      </>
                    )}
                    <p className="font-semibold text-gray-900">Total: {formatEuro(totais.total)}</p>
                  </div>
                );
              })()}
            </div>

            <div>
              <h3 className="font-semibold text-gray-900">Opções de pagamento exibidas ao cliente *</h3>
              <p className="text-xs text-gray-500 mt-1 mb-3">
                O cliente vê todas as opções habilitadas com o valor final calculado e escolhe uma ao aceitar o orçamento.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-3 p-3 mb-3 border rounded-lg bg-gray-50">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Abatimento manual (€)</label>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={formData.abatimentoManual?.valor || ''}
                    placeholder="0.00"
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        abatimentoManual: {
                          descricao: prev.abatimentoManual?.descricao || '',
                          valor: Math.max(parseFloat(e.target.value) || 0, 0)
                        }
                      }))
                    }
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Descrição (opcional, visível ao cliente)</label>
                  <input
                    value={formData.abatimentoManual?.descricao || ''}
                    placeholder="Ex.: Análise Personalizada já paga"
                    maxLength={200}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        abatimentoManual: { valor: prev.abatimentoManual?.valor || 0, descricao: e.target.value }
                      }))
                    }
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 bg-white"
                  />
                </div>
                <p className="sm:col-span-2 text-xs text-gray-500">
                  Descontado do total antes de calcular as opções abaixo. Total com abatimento:{' '}
                  <strong className="text-gray-900">{formatEuro(computeTotal(formData.itens, formData.abatimentoManual))}</strong>
                </p>
              </div>
              <div className="space-y-3">
                <label className="flex flex-wrap items-center gap-3 p-3 border rounded-lg">
                  <input
                    type="checkbox"
                    checked={opcoes.avista.habilitado}
                    onChange={(e) => updateOpcoes((o) => ({ ...o, avista: { habilitado: e.target.checked } }))}
                  />
                  <span className="font-medium text-gray-900">À vista</span>
                  <span className="ml-auto text-sm text-gray-600">{formatEuro(computeTotal(formData.itens, formData.abatimentoManual))}</span>
                </label>

                <div className="flex flex-wrap items-center gap-3 p-3 border rounded-lg">
                  <input
                    type="checkbox"
                    id="opcao-avista-desconto"
                    checked={opcoes.avistaDesconto.habilitado}
                    onChange={(e) =>
                      updateOpcoes((o) => ({ ...o, avistaDesconto: { ...o.avistaDesconto, habilitado: e.target.checked } }))
                    }
                  />
                  <label htmlFor="opcao-avista-desconto" className="font-medium text-gray-900">
                    À vista com desconto
                  </label>
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step={0.01}
                      value={opcoes.avistaDesconto.percentual}
                      disabled={!opcoes.avistaDesconto.habilitado}
                      onChange={(e) =>
                        updateOpcoes((o) => ({
                          ...o,
                          avistaDesconto: { ...o.avistaDesconto, percentual: Number(e.target.value) || 0 }
                        }))
                      }
                      className="w-24 px-2 py-1 border rounded-md focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                    />
                    <span className="text-sm text-gray-600">%</span>
                  </div>
                  {opcoes.avistaDesconto.habilitado && (() => {
                    const opc = calcularOpcoes(computeTotal(formData.itens, formData.abatimentoManual), opcoes).find((x) => x.id === 'avista_desconto');
                    return opc ? (
                      <span className="ml-auto text-sm text-gray-600">
                        {formatEuro(opc.valorFinal)}{' '}
                        <span className="text-green-700">(−{formatEuro(opc.valorBase - opc.valorFinal)})</span>
                      </span>
                    ) : null;
                  })()}
                </div>

                <div className="p-3 border rounded-lg">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-medium text-gray-900">Parcelado com juros</span>
                    <button
                      type="button"
                      onClick={addParcelado}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-600 hover:text-primary-800"
                    >
                      <Plus className="w-4 h-4" /> Adicionar parcelamento
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Juros em % sobre o valor total, dividido igualmente entre as parcelas (0% = sem juros).
                  </p>
                  {opcoes.parcelados.length > 0 && (
                    <div className="space-y-2 mt-3">
                      {opcoes.parcelados.map((p, index) => {
                        const opc = calcularOpcoes(computeTotal(formData.itens, formData.abatimentoManual), {
                          parcelados: [p]
                        })[0];
                        return (
                          <div key={index} className="flex flex-wrap items-center gap-3">
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                min={MIN_PARCELAS}
                                max={MAX_PARCELAS}
                                value={p.parcelas || ''}
                                onChange={(e) => updateParcelado(index, 'parcelas', e.target.value)}
                                className="w-20 px-2 py-1 border rounded-md focus:ring-2 focus:ring-blue-500"
                                aria-label="Número de parcelas"
                              />
                              <span className="text-sm text-gray-600">x</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                min={0}
                                max={100}
                                step={0.01}
                                value={p.juros}
                                onChange={(e) => updateParcelado(index, 'juros', e.target.value)}
                                className="w-24 px-2 py-1 border rounded-md focus:ring-2 focus:ring-blue-500"
                                aria-label="Juros (%)"
                              />
                              <span className="text-sm text-gray-600">% juros</span>
                            </div>
                            <span className="text-sm text-gray-600">
                              {opc
                                ? `${opc.parcelas}x de ${formatEuro(opc.valorParcela)} · total ${formatEuro(opc.valorFinal)}`
                                : `Informe de ${MIN_PARCELAS} a ${MAX_PARCELAS} parcelas`}
                            </span>
                            <button
                              type="button"
                              onClick={() => removeParcelado(index)}
                              className="ml-auto p-1.5 text-red-600 hover:bg-red-100 rounded-lg transition-colors duration-200"
                              title="Remover parcelamento"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Texto do orçamento (pt/en/es)
              </label>
              <I18nTripleFields mode="orcamento" value={i18n} onChange={setI18n} />
            </div>

            <div className="flex flex-wrap justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors duration-200"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    <span>Salvando...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Salvar</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {!showForm && (
        <>
          <div className="mb-4 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por nome ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as OrcamentoStatus | '')}
              className="px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 bg-white"
              aria-label="Filtrar por status"
            >
              <option value="">Todos os status</option>
              {(Object.keys(STATUS_LABEL) as OrcamentoStatus[]).map((st) => (
                <option key={st} value={st}>
                  {STATUS_LABEL[st].label}
                </option>
              ))}
            </select>
            <select
              value={origemFilter}
              onChange={(e) => setOrigemFilter(e.target.value as OrigemCliente | '')}
              className="px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 bg-white"
              aria-label="Filtrar por origem do cliente"
            >
              <option value="">Todas as origens</option>
              {(Object.keys(ORIGEM_CLIENTE_LABEL) as OrigemCliente[]).map((k) => (
                <option key={k} value={k}>
                  {ORIGEM_CLIENTE_LABEL[k]}
                </option>
              ))}
            </select>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
            </div>
          ) : orcamentos.length === 0 ? (
            <p className="text-gray-500 text-center py-12">Nenhum orçamento. Clique em &quot;Novo orçamento&quot;.</p>
          ) : (
            <div className="space-y-4">
              {orcamentos.map((o) => {
                const status = o.status || 'draft';
                const statusInfo = STATUS_LABEL[status];
                return (
                  <div
                    key={o._id}
                    className="bg-white rounded-lg shadow p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                  >
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {temInfoInterna(o) ? (
                          <div className="relative group">
                            <button
                              type="button"
                              onClick={() => openInterno(o)}
                              className="flex items-center gap-1.5 font-semibold text-gray-900 underline decoration-dotted decoration-amber-400 underline-offset-4 hover:text-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 rounded"
                              aria-label={`${o.nomeCliente} — ver ou editar notas internas`}
                            >
                              {o.nomeCliente}
                              <StickyNote className="w-4 h-4 text-amber-500" aria-hidden="true" />
                            </button>
                            <div
                              role="tooltip"
                              className="invisible opacity-0 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100 transition-opacity duration-150 absolute left-0 top-full z-30 mt-2 w-80 max-w-[calc(100vw-3rem)] rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-gray-800 shadow-lg"
                            >
                              <p className="flex items-center gap-1 font-medium text-amber-800 mb-1">
                                <Lock className="w-3 h-3" /> Uso interno
                              </p>
                              {labelOrigem(o) && (
                                <p>
                                  <span className="font-medium">Origem:</span> {labelOrigem(o)}
                                </p>
                              )}
                              {o.notasInternas && (
                                <p className="mt-1 whitespace-pre-line max-h-48 overflow-y-auto">{o.notasInternas}</p>
                              )}
                              <p className="mt-2 text-[11px] text-amber-700">Clique no nome para editar</p>
                            </div>
                          </div>
                        ) : (
                          <p className="font-semibold text-gray-900">{o.nomeCliente}</p>
                        )}
                        <span className={`px-2 py-0.5 text-xs rounded-full ${statusInfo.className}`}>
                          {statusInfo.label}
                        </span>
                        {o.origemCliente && (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-amber-50 text-amber-800 border border-amber-200">
                            {ORIGEM_CLIENTE_LABEL[o.origemCliente]}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{o.emailCliente || 'Sem e-mail cadastrado'}</p>
                      <p className="text-xs text-gray-500">
                        Total: €{(o.total ?? computeTotal(o.itens)).toFixed(2)} ·{' '}
                        {o.opcaoEscolhida
                          ? `Escolhido: ${labelOpcaoAdmin(o.opcaoEscolhida)}`
                          : o.opcoesCalculadas && o.opcoesCalculadas.length > 0
                            ? `${o.opcoesCalculadas.length} opç${o.opcoesCalculadas.length > 1 ? 'ões' : 'ão'} de pagamento`
                            : labelFormaPagamento(o.formaPagamento)}
                      </p>
                      {o.createdAt && (
                        <p className="text-xs text-gray-500">{new Date(o.createdAt).toLocaleDateString('pt-PT')}</p>
                      )}
                      {o.recusa && (
                        <div className="mt-2 text-xs rounded-md bg-red-50 border border-red-100 px-3 py-2 text-red-800">
                          <p>
                            <span className="font-medium">
                              {status === 'declined' ? 'Motivo da recusa' : 'Recusou anteriormente'}:
                            </span>{' '}
                            {MOTIVO_RECUSA_LABEL[o.recusa.motivo] || o.recusa.motivo}
                            {o.recusa.recusadoAt && (
                              <span className="text-red-600"> · {new Date(o.recusa.recusadoAt).toLocaleDateString('pt-PT')}</span>
                            )}
                          </p>
                          {o.recusa.comentario && (
                            <p className="mt-1 italic whitespace-pre-line">&ldquo;{o.recusa.comentario}&rdquo;</p>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => openInterno(o)}
                        className="p-2 text-amber-600 hover:bg-amber-100 rounded-lg transition-colors duration-200"
                        title={temInfoInterna(o) ? 'Notas internas' : 'Adicionar origem / notas internas'}
                      >
                        <StickyNote className="w-5 h-5" />
                      </button>
                      {!['accepted', 'converted'].includes(status) && (
                        <button
                          onClick={() => openEdit(o)}
                          className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors duration-200"
                          title="Editar"
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                      )}
                      {status === 'sent' || status === 'expired' ? (
                        <button
                          onClick={() => openViewLink(o)}
                          className="p-2 text-amber-600 hover:bg-amber-100 rounded-lg transition-colors duration-200"
                          title="Ver link"
                        >
                          <LinkIcon className="w-5 h-5" />
                        </button>
                      ) : status === 'draft' || status === 'declined' ? (
                        <button
                          onClick={() => o._id && openSendLink(o._id)}
                          className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors duration-200"
                          title="Gerar link para o cliente"
                        >
                          <Send className="w-5 h-5" />
                        </button>
                      ) : null}
                      {!['accepted', 'converted'].includes(status) && (
                        <button
                          onClick={() => marcarAceito(o)}
                          className="p-2 text-emerald-600 hover:bg-emerald-100 rounded-lg transition-colors duration-200"
                          title="Marcar como aceito manualmente"
                        >
                          <CheckCircle2 className="w-5 h-5" />
                        </button>
                      )}
                      {status === 'accepted' && (
                        <button
                          onClick={() => o._id && converterEmContrato(o._id)}
                          disabled={converting === o._id}
                          className="p-2 text-purple-600 hover:bg-purple-100 rounded-lg transition-colors duration-200 disabled:opacity-50"
                          title="Converter em contrato"
                        >
                          {converting === o._id ? (
                            <div className="w-5 h-5 rounded-full border-b-2 border-purple-600 animate-spin" />
                          ) : (
                            <ArrowRightCircle className="w-5 h-5" />
                          )}
                        </button>
                      )}
                      {status !== 'converted' && (
                        <button
                          onClick={() => o._id && handleDelete(o._id)}
                          className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors duration-200"
                          title="Excluir"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {internoOrcamento && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full sm:max-w-lg p-6">
            <div className="flex justify-between items-center mb-1">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <StickyNote className="w-5 h-5 text-amber-500" />
                Notas internas
              </h3>
              <button
                onClick={() => setInternoOrcamento(null)}
                className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                title="Fechar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              {internoOrcamento.nomeCliente} · visível só para a equipe
            </p>
            <CamposInternos value={internoForm} onChange={setInternoForm} />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setInternoOrcamento(null)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors duration-200"
              >
                Cancelar
              </button>
              <button
                onClick={salvarInterno}
                disabled={salvandoInterno}
                className="btn-primary flex items-center gap-2 disabled:opacity-50"
              >
                {salvandoInterno && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {aceiteOrcamento && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full sm:max-w-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5" />
                Marcar como aceito
              </h3>
              <button
                onClick={() => setAceiteOrcamento(null)}
                className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                title="Fechar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Qual forma de pagamento {aceiteOrcamento.nomeCliente} escolheu?
            </p>
            <div className="space-y-2 mb-6">
              {(aceiteOrcamento.opcoesCalculadas || []).map((opc) => (
                <label
                  key={opc.id}
                  className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer ${
                    aceiteOpcaoId === opc.id ? 'border-primary-500 bg-primary-50' : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="aceite-opcao"
                    checked={aceiteOpcaoId === opc.id}
                    onChange={() => setAceiteOpcaoId(opc.id)}
                  />
                  <span className="flex-1 text-sm text-gray-900">{labelOpcaoAdmin(opc)}</span>
                  <span className="text-sm font-medium text-gray-900">{formatEuro(opc.valorFinal)}</span>
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setAceiteOrcamento(null)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors duration-200"
              >
                Cancelar
              </button>
              <button
                onClick={() => aceiteOrcamento._id && postMarcarAceito(aceiteOrcamento._id, aceiteOpcaoId)}
                disabled={marcandoAceito || !aceiteOpcaoId}
                className="btn-primary flex items-center gap-2 disabled:opacity-50"
              >
                {marcandoAceito && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
                Confirmar aceite
              </button>
            </div>
          </div>
        </div>
      )}

      {showLinkModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full sm:max-w-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Send className="w-5 h-5" />
                {viewUrlPreview ? 'Link do orçamento' : 'Gerar link para o cliente'}
              </h3>
              <button
                onClick={() => setShowLinkModal(false)}
                className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                title="Fechar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {!viewUrlPreview ? (
              <>
                <p className="text-sm text-gray-600 mb-4">
                  {orcamentos.find((o) => o._id === linkOrcamentoId)?.emailCliente
                    ? 'Um link será enviado por e-mail ao cliente para revisar e aceitar o orçamento, sem necessidade de conta.'
                    : 'Este orçamento não tem e-mail cadastrado — o link será gerado para você copiar e enviar manualmente (WhatsApp, etc.).'}
                </p>
                <label className="block mb-4">
                  <span className="block text-sm font-medium text-gray-700 mb-1">Validade do link (dias)</span>
                  <input
                    type="number"
                    min={1}
                    max={90}
                    value={expiresInDays}
                    onChange={(e) => setExpiresInDays(parseInt(e.target.value, 10) || 30)}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </label>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowLinkModal(false)}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors duration-200"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={confirmSendLink}
                    disabled={sendingLink}
                    className="btn-primary flex items-center gap-2 disabled:opacity-50"
                  >
                    {sendingLink && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
                    Enviar
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-600 mb-2">Link do orçamento para o cliente:</p>
                <div className="flex items-center gap-2 mb-4">
                  <input
                    readOnly
                    value={viewUrlPreview}
                    className="flex-1 px-3 py-2 border rounded-md bg-gray-50 text-sm"
                    onFocus={(e) => e.target.select()}
                  />
                  <button
                    onClick={copyViewUrl}
                    className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors duration-200"
                    title="Copiar link"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                </div>
                {linkCopied && <p className="text-xs text-green-600 mb-4">Link copiado!</p>}
                <div className="flex justify-end gap-3">
                  <button
                    onClick={revokeLink}
                    className="px-4 py-2 text-red-600 border border-red-300 rounded-md hover:bg-red-50 transition-colors duration-200 flex items-center gap-2"
                  >
                    <Ban className="w-4 h-4" /> Revogar link
                  </button>
                  <button onClick={() => setShowLinkModal(false)} className="btn-primary">
                    Fechar
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default AdminOrcamento;
